// Squarified treemap layout (Bruls, Huizing, van Wijk 2000).
// Pure function, no React dependency — easy to unit test.

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Tile<T> {
  item: T;
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Lay out `data` inside `bounds` so each item's area is proportional to its value.
 * Items with value <= 0 are skipped. Input order is preserved as best-effort;
 * callers should pass items pre-sorted by value descending for nicer results.
 */
export function squarify<T>(
  data: T[],
  getValue: (t: T) => number,
  bounds: Rect
): Tile<T>[] {
  const results: Tile<T>[] = [];

  // Non-linear compression (gamma=0.5): eases extreme ratios so tiny tiles stay
  // visible, while still preserving order (bigger value -> bigger area always).
  const GAMMA = 0.5;
  const items = data
    .map((item) => {
      const value = Math.max(0, getValue(item));
      return { item, value, weight: value > 0 ? Math.pow(value, GAMMA) : 0 };
    })
    .filter((d) => d.value > 0);

  const weightSum = items.reduce((s, d) => s + d.weight, 0);
  if (weightSum <= 0 || bounds.w <= 0 || bounds.h <= 0) return results;

  // Min-area floor: guarantee every tile occupies at least `floor` of the area,
  // so extreme ratios (e.g. 1900:1) can't shrink a tile below clickable size —
  // the deficit is borrowed from the big tiles. Adapt `floor` down as the tile
  // count grows so the reserved total never exceeds 50%. Displayed sizes/percent
  // still use the real value; this only affects the visual area.
  const n = items.length;
  const MIN_FRAC = 0.015;
  const floor = Math.min(MIN_FRAC, 0.5 / n);
  const totalArea = bounds.w * bounds.h;
  const scaled = items.map((d) => ({
    item: d.item,
    area: (floor + (1 - floor * n) * (d.weight / weightSum)) * totalArea,
  }));

  const worstRatio = (row: { area: number }[], side: number): number => {
    if (row.length === 0) return Infinity;
    let sum = 0;
    let max = -Infinity;
    let min = Infinity;
    for (const r of row) {
      sum += r.area;
      if (r.area > max) max = r.area;
      if (r.area < min) min = r.area;
    }
    const side2 = side * side;
    const sum2 = sum * sum;
    return Math.max((side2 * max) / sum2, sum2 / (side2 * min));
  };

  // Place a finished row along the short side, return the leftover rect.
  const layoutRow = (row: { item: T; area: number }[], rect: Rect): Rect => {
    const sum = row.reduce((s, r) => s + r.area, 0);
    if (sum <= 0) return rect;

    if (rect.w >= rect.h) {
      // Stack vertically in a column of width `colW` on the left.
      const colW = sum / rect.h;
      let y = rect.y;
      for (const r of row) {
        const tileH = r.area / colW;
        results.push({ item: r.item, x: rect.x, y, w: colW, h: tileH });
        y += tileH;
      }
      return { x: rect.x + colW, y: rect.y, w: rect.w - colW, h: rect.h };
    } else {
      // Stack horizontally in a row of height `rowH` on the top.
      const rowH = sum / rect.w;
      let x = rect.x;
      for (const r of row) {
        const tileW = r.area / rowH;
        results.push({ item: r.item, x, y: rect.y, w: tileW, h: rowH });
        x += tileW;
      }
      return { x: rect.x, y: rect.y + rowH, w: rect.w, h: rect.h - rowH };
    }
  };

  let rect: Rect = { ...bounds };
  let row: { item: T; area: number }[] = [];

  for (const d of scaled) {
    const side = Math.min(rect.w, rect.h);
    const withItem = [...row, d];
    if (row.length === 0 || worstRatio(withItem, side) <= worstRatio(row, side)) {
      row = withItem;
    } else {
      rect = layoutRow(row, rect);
      row = [d];
    }
  }
  if (row.length > 0) layoutRow(row, rect);

  return results;
}
